/**
 * EntityCard - Display a Digital Twin entity
 *
 * Shows entity properties and linked documents. Used both inline
 * (lightweight summary) and in side panel (full details).
 */

import React from 'react';
import {
  Tractor,
  Users,
  Warehouse,
  Map,
  PiggyBank,
  FileText,
  ExternalLink,
  AlertCircle,
  Upload,
} from 'lucide-react';
import type { MockEntity, MockDocument } from '../../types';
import './EntityCard.css';

export type EntityCardVariant = 'inline' | 'panel';

export interface EntityCardProps {
  /** The entity to display */
  entity: MockEntity;

  /** Display variant */
  variant?: EntityCardVariant;

  /** Callback when clicking the full view action */
  onViewFull?: (entityId: string) => void;

  /** Callback when clicking a document */
  onDocumentClick?: (documentId: string) => void;

  /** Callback when clicking upload document */
  onUploadDocument?: (entityId: string) => void;

  /** Additional CSS class */
  className?: string;
}

const entityTypeIcons: Record<string, React.ReactNode> = {
  field: <Map size={18} />,
  animal_group: <PiggyBank size={18} />,
  equipment: <Tractor size={18} />,
  person: <Users size={18} />,
  storage: <Warehouse size={18} />,
};

const entityTypeLabels: Record<string, string> = {
  field: 'Schlag',
  animal_group: 'Tierbestand',
  equipment: 'Gerät',
  person: 'Person',
  storage: 'Lager',
};

/**
 * Upload button labels based on entity type.
 * Each entity type has a contextually appropriate upload action.
 */
const uploadButtonLabels: Record<string, string> = {
  field: 'Bodenprobe hochladen',
  animal_group: 'Bestandsmeldung hochladen',
  equipment: 'Prüfbericht hochladen',
  person: 'Nachweis hochladen',
  storage: 'Dokument hochladen',
};

const formatPropertyValue = (_key: string, value: unknown): string => {
  if (value instanceof Date) {
    return value.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
  if (typeof value === 'number') {
    return value.toLocaleString('de-DE');
  }
  if (typeof value === 'boolean') {
    return value ? 'Ja' : 'Nein';
  }
  return String(value);
};

const formatPropertyLabel = (key: string): string => {
  // Common property translations
  const translations: Record<string, string> = {
    size: 'Größe',
    usage: 'Nutzung',
    soilType: 'Bodenart',
    lastSoilSample: 'Letzte Bodenprobe',
    nRequirement: 'N-Bedarf',
    nApplied2025: 'Gedüngt 2025',
    count: 'Anzahl',
    breed: 'Rasse',
    avgWeight: 'Ø Gewicht',
    buildingType: 'Stalltyp',
    capacity: 'Kapazität',
    licensePlate: 'Kennzeichen',
    tuevValid: 'TÜV gültig bis',
    lastInspection: 'Letzte Prüfung',
    manufacturer: 'Hersteller',
    model: 'Modell',
    yearBuilt: 'Baujahr',
    name: 'Name',
    role: 'Position',
    certificationValid: 'Zertifikat gültig',
    lastBriefing: 'Letzte Unterweisung',
  };

  return translations[key] || key;
};

const isDateExpired = (key: string, value: unknown): boolean => {
  if (!(value instanceof Date)) return false;
  const warningKeys = ['lastSoilSample', 'tuevValid', 'certificationValid', 'lastBriefing'];
  if (!warningKeys.includes(key)) return false;
  return value < new Date();
};

export const EntityCard: React.FC<EntityCardProps> = ({
  entity,
  variant = 'panel',
  onViewFull,
  onDocumentClick,
  onUploadDocument,
  className = '',
}) => {
  const icon = entityTypeIcons[entity.type] || <Map size={18} />;
  const typeLabel = entityTypeLabels[entity.type] || entity.type;

  // Filter out internal properties
  const displayProperties = Object.entries(entity.properties).filter(
    ([key]) => !key.startsWith('_')
  );

  const formatDate = (date: Date | string): string => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  if (variant === 'inline') {
    return (
      <div className={`entity-card entity-card--inline ${className}`}>
        <div className="entity-card__inline-header">
          <span className="entity-card__inline-icon" aria-hidden="true">
            {icon}
          </span>
          <span className="entity-card__inline-name">{entity.name}</span>
          <span className="entity-card__inline-type">{typeLabel}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`entity-card entity-card--panel ${className}`}>
      {/* Header */}
      <header className="entity-card__header">
        <div className="entity-card__header-icon" aria-hidden="true">
          {icon}
        </div>
        <div className="entity-card__header-content">
          <h3 className="entity-card__name">{entity.name}</h3>
          <span className="entity-card__type">{typeLabel}</span>
        </div>
      </header>

      {/* Properties */}
      <section className="entity-card__properties">
        <h4 className="entity-card__section-title">Eigenschaften</h4>
        <dl className="entity-card__property-list">
          {displayProperties.map(([key, value]) => {
            const expired = isDateExpired(key, value);
            return (
              <div
                key={key}
                className={`entity-card__property ${expired ? 'entity-card__property--warning' : ''}`}
              >
                <dt className="entity-card__property-label">{formatPropertyLabel(key)}</dt>
                <dd className="entity-card__property-value">
                  {expired && <AlertCircle size={14} className="entity-card__warning-icon" />}
                  {formatPropertyValue(key, value)}
                </dd>
              </div>
            );
          })}
        </dl>
      </section>

      {/* Documents */}
      {entity.documents && entity.documents.length > 0 && (
        <section className="entity-card__documents">
          <h4 className="entity-card__section-title">Verknüpfte Dokumente</h4>
          <ul className="entity-card__document-list">
            {entity.documents.map((doc: MockDocument) => (
              <li key={doc.id} className="entity-card__document-item">
                <button
                  type="button"
                  className="entity-card__document-button"
                  onClick={() => onDocumentClick?.(doc.id)}
                >
                  <FileText size={14} className="entity-card__document-icon" />
                  <span className="entity-card__document-name">{doc.name}</span>
                  {doc.date && (
                    <span className="entity-card__document-date">{formatDate(doc.date)}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Actions */}
      {(onViewFull || onUploadDocument) && (
        <footer className="entity-card__actions">
          {onUploadDocument && (
            <button
              type="button"
              className="entity-card__upload-button"
              onClick={() => onUploadDocument(entity.id)}
            >
              <Upload size={14} />
              {uploadButtonLabels[entity.type] || 'Dokument hochladen'}
            </button>
          )}
          {onViewFull && (
            <button
              type="button"
              className="entity-card__view-full"
              onClick={() => onViewFull(entity.id)}
            >
              <ExternalLink size={14} />
              In Mein Hof anzeigen
            </button>
          )}
        </footer>
      )}
    </div>
  );
};

export default EntityCard;
